import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Text, Card, Button } from '@hawkins/components';
import { InputFormControl, FormProvider, useForm } from '@hawkins/forms';
import { useTranslation } from 'react-i18next';
import NetflixGradientBackground from 'src/components/elements/NetflixGradientBackground';
import LoadingText from 'src/components/elements/LoadingText';
import { getErrorMessage } from 'src/utils/errorHandler';

const REDIRECT_DELAY_MS = 5000;

// window.location.replace executes javascript: URLs, and this origin holds the reset
// token and the MFA cookie. Keep this guard even though the server also checks.
const navigateToTarget = (url: string): void => {
  try {
    if (new URL(url).protocol !== 'https:') return;
  } catch {
    return;
  }
  window.location.replace(url);
};

const NetflixLogo = () => (
  <div style={{ marginBottom: '40px' }}>
    <img 
      src="/nflx-logo-classic@150w.png" 
      alt="Netflix" 
      style={{ height: '80px' }}
    />
  </div>
);

const LoadingState = () => {
  const { t } = useTranslation();
  return (
    <LoadingText>
      {t('common.status.loading')}
    </LoadingText>
  );
};

const ErrorDisplay = ({ error }: { error: string }) => (
  <Card style={{
    backgroundColor: '#660000',
    border: '1px solid #cc0000',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#ffcccc', fontSize: '18px', textAlign: 'center' }}>
      {error}
    </Text>
  </Card>
);

interface PasswordPolicy {
  type: string;
  minLength: number;
  requiresDigits: boolean;
  requiresUppercase: boolean;
  requiresLowercase: boolean;
  requiresSpecialChars: boolean;
  description: string;
}

interface PasswordRequirementProps {
  met: boolean;
  children: React.ReactNode;
}

const PasswordRequirement = ({ met, children }: PasswordRequirementProps) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
    <span style={{ 
      color: met ? '#00cc00' : '#999', 
      marginInlineEnd: '8px',
      fontSize: '14px',
      width: '20px'
    }}>
      {met ? '✓' : '○'}
    </span>
    <Text style={{ color: met ? '#00cc00' : '#999', fontSize: '14px' }}>
      {children}
    </Text>
  </div>
);

interface PasswordRequirementsDisplayProps {
  passwordPolicy: PasswordPolicy;
  password: string;
}

const PasswordRequirementsDisplay = ({ passwordPolicy, password }: PasswordRequirementsDisplayProps) => {
  const { t } = useTranslation();
  const requirements = useMemo(() => {
    const hasMinLength = password.length >= passwordPolicy.minLength;
    const hasDigit = passwordPolicy.requiresDigits ? /\d/.test(password) : true;
    const hasUppercase = passwordPolicy.requiresUppercase ? /[A-Z]/.test(password) : true;
    const hasLowercase = passwordPolicy.requiresLowercase ? /[a-z]/.test(password) : true;
    const hasSpecialChar = passwordPolicy.requiresSpecialChars ? /[!@#$%^&*()+=._\-[\]{}|;':",./<>?~`]/.test(password) : true;

    return {
      minLength: hasMinLength,
      digits: hasDigit,
      uppercase: hasUppercase,
      lowercase: hasLowercase,
      specialChars: hasSpecialChar,
      allMet: hasMinLength && hasDigit && hasUppercase && hasLowercase && hasSpecialChar
    };
  }, [password, passwordPolicy]);

  return (
    <Card style={{
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      border: '1px solid #333',
      borderRadius: '4px',
      padding: '16px',
      marginBottom: '24px'
    }}>
      <Text style={{ color: 'white', fontSize: '16px', marginBottom: '16px', fontWeight: '500' }}>
        {t('auth.passwordRequirements.title')}
      </Text>
      
      <PasswordRequirement met={requirements.minLength}>
        {t('auth.passwordRequirements.minLength', { count: passwordPolicy.minLength })}
      </PasswordRequirement>
      
      {passwordPolicy.requiresDigits && (
        <PasswordRequirement met={requirements.digits}>
          {t('auth.passwordRequirements.requiresDigits')}
        </PasswordRequirement>
      )}
      
      {passwordPolicy.requiresUppercase && (
        <PasswordRequirement met={requirements.uppercase}>
          {t('auth.passwordRequirements.requiresUppercase')}
        </PasswordRequirement>
      )}
      
      {passwordPolicy.requiresLowercase && (
        <PasswordRequirement met={requirements.lowercase}>
          {t('auth.passwordRequirements.requiresLowercase')}
        </PasswordRequirement>
      )}
      
      {passwordPolicy.requiresSpecialChars && (
        <PasswordRequirement met={requirements.specialChars}>
          {t('auth.passwordRequirements.requiresSpecialChars')}
        </PasswordRequirement>
      )}
    </Card>
  );
};

interface PolicyData {
  passwordPolicy: PasswordPolicy;
}

interface FormData {
  password: string;
  confirmPassword: string;
}

function ResetPasswordForm() {
  const { t } = useTranslation();
  const { resetToken } = useParams<{ resetToken: string }>();
  const navigate = useNavigate();
  const [policyData, setPolicyData] = useState<PolicyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [mfaSessionExpired, setMfaSessionExpired] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  const methods = useForm<FormData>({
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });

  const { watch, formState } = methods;
  const passwordValue = watch('password');
  const confirmPasswordValue = watch('confirmPassword');

  const loadPasswordPolicy = useCallback(async () => {
    if (!resetToken) {
      navigate('/');
      return;
    }

    setLoading(true);
    
    try {
      // Fetch password policy for validation
      const response = await fetch('/api/verify-reset-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetToken
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.passwordPolicy) {
        setPolicyData({
          passwordPolicy: data.passwordPolicy
        });
      } else {
        // Token became invalid or something went wrong - redirect back
        navigate('/');
      }
    } catch (error) {
      // Network error or server issue - redirect back
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [resetToken, navigate]);

  const handlePasswordReset = async (formData: FormData) => {
    if (!resetToken || !policyData) return;

    setSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch('/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetToken,
          password: formData.password
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check if this is a partial failure
        if (data.partialFailure) {
          // Handle partial sync failure with translated message
          const syncFailureMessage = t('errors.PASSWORD_RESET_SUCCESS_PARTIAL_SYNC_FAILURE', {
            systems: data.syncFailures?.join(', ') || 'some systems'
          });
          setSubmitError(syncFailureMessage);
        } else {
          // Absent when Pandora has no target for this token; stay on the success screen.
          setRedirectUrl(data.targetAppUrl || null);
          setSubmitSuccess(true);
        }
      } else {
        const errorData = await response.json();
        
        // Check if MFA session has expired
        if (errorData.errorCode === 'MFA_COMPLETION_REQUIRED') {
          setMfaSessionExpired(true);
          setSubmitError(null); // Clear regular error since we'll show MFA expired state
        } else {
          setSubmitError(getErrorMessage(t, errorData));
        }
      }
    } catch (error) {
      setSubmitError(t('errors.network'));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    loadPasswordPolicy();
  }, [loadPasswordPolicy]);

  useEffect(() => {
    if (!submitSuccess || !redirectUrl) return;

    const timer = setTimeout(() => {
      navigateToTarget(redirectUrl);
    }, REDIRECT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [submitSuccess, redirectUrl]);

  if (loading) {
    return (
      <NetflixGradientBackground>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: '450px'
          }}>
            <NetflixLogo />
            <Card style={{
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              padding: '48px 68px 36px 68px',
              borderRadius: '4px',
              width: '100%',
              border: 'none'
            }}>
              <LoadingState />
            </Card>
          </div>
        </div>

      </NetflixGradientBackground>
    );
  }

  if (submitSuccess) {
    return (
      <NetflixGradientBackground>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: '450px'
          }}>
            <NetflixLogo />
            <Card style={{
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              padding: '48px 68px 36px 68px',
              borderRadius: '4px',
              width: '100%',
              border: 'none'
            }}>
              <div style={{ marginBottom: '24px', fontSize: '48px', textAlign: 'center' }}>✓</div>
              <Text 
                id="success-title" // DO NOT CHANGE: Used by E2E tests - https://github.netflix.net/corp/iac-login-flow-test/blob/main/src/main/kotlin/com/netflix/infosec/meechum/page/passwordreset/PasswordResetPage.kt
                style={{ 
                  color: 'white', 
                  fontSize: '24px',
                  fontWeight: '700',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}
              >
                {t('auth.resetPassword.successTitle')}
              </Text>
              <Text style={{ 
                color: '#999', 
                fontSize: '16px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                {t('auth.resetPassword.successMessage')}
              </Text>
              {redirectUrl && (
                <Button
                  onClick={() => navigateToTarget(redirectUrl)}
                  style={{
                    width: '100%',
                    backgroundColor: '#e50914',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '700',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    height: '48px',
                    minHeight: '48px',
                    boxSizing: 'border-box'
                  }}
                >
                  {t('common.actions.continue')}
                </Button>
              )}
            </Card>
          </div>
        </div>

      </NetflixGradientBackground>
    );
  }

  if (mfaSessionExpired) {
    return (
      <NetflixGradientBackground>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: '450px'
          }}>
            <NetflixLogo />
            <Card style={{
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              padding: '48px 68px 36px 68px',
              borderRadius: '4px',
              width: '100%',
              border: 'none'
            }}>
              <div style={{ marginBottom: '24px', fontSize: '48px', textAlign: 'center' }}>⏰</div>
              <Text 
                style={{ 
                  color: 'white', 
                  fontSize: '24px',
                  fontWeight: '700',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}
              >
                {t('auth.resetPassword.mfaSessionExpiredTitle')}
              </Text>
              <Text style={{ 
                color: '#999', 
                fontSize: '16px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                {t('auth.resetPassword.mfaSessionExpiredMessage')}
              </Text>
              <Button
                onClick={() => navigate(`/reset/${resetToken}`)}
                style={{
                  width: '100%',
                  backgroundColor: '#e50914',
                  border: 'none',
                  borderRadius: '4px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '700',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  height: '48px',
                  minHeight: '48px',
                  boxSizing: 'border-box'
                }}
              >
                {t('auth.resetPassword.startOverButton')}
              </Button>
            </Card>
          </div>
        </div>

      </NetflixGradientBackground>
    );
  }

  if (!policyData) {
    // This should not happen since we redirect on error, but just in case
    return null;
  }

  return (
    <NetflixGradientBackground>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '450px'
        }}>
          <NetflixLogo />

          <Card style={{
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            padding: '48px 68px 36px 68px',
            borderRadius: '4px',
            width: '100%',
            border: 'none'
          }}>
            <Text 
              style={{ 
                color: 'white', 
                fontSize: '32px',
                fontWeight: '700',
                marginBottom: '24px',
                textAlign: 'center'
              }}
            >
              {t('auth.resetPassword.titleFull')}
            </Text>

            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(handlePasswordReset)}>
                <Text style={{ 
                  color: '#999', 
                  fontSize: '16px', 
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  {t('auth.resetPassword.createPasswordMessage')}
                </Text>

                <PasswordRequirementsDisplay 
                  passwordPolicy={policyData.passwordPolicy}
                  password={passwordValue || ''}
                />

                <div style={{ minHeight: '90px', marginBottom: '20px' }}>
                  <InputFormControl<FormData>
                    name="password"
                    id="password" // DO NOT CHANGE: Used by E2E tests - https://github.netflix.net/corp/iac-login-flow-test/blob/main/src/main/kotlin/com/netflix/infosec/meechum/page/passwordreset/PasswordResetPage.kt
                    label={t('auth.resetPassword.newPasswordLabel')}
                    type="password"
                    validate={({ value }) => {
                      if (!value) return { message: t('auth.validation.passwordRequired'), type: 'error' };
                      
                      const password = String(value);
                      const policy = policyData.passwordPolicy;
                      
                      if (password.length < policy.minLength) {
                        return { message: t('auth.validation.passwordMinLength', { count: policy.minLength }), type: 'error' };
                      }
                      
                      if (policy.requiresDigits && !/\d/.test(password)) {
                        return { message: t('auth.validation.passwordNeedsDigit'), type: 'error' };
                      }
                      
                      if (policy.requiresUppercase && !/[A-Z]/.test(password)) {
                        return { message: t('auth.validation.passwordNeedsUppercase'), type: 'error' };
                      }
                      
                      if (policy.requiresLowercase && !/[a-z]/.test(password)) {
                        return { message: t('auth.validation.passwordNeedsLowercase'), type: 'error' };
                      }
                      
                      if (policy.requiresSpecialChars && !/[!@#$%^&*()+=._\-[\]{}|;':",./<>?~`]/.test(password)) {
                        return { message: t('auth.validation.passwordNeedsSpecialChar'), type: 'error' };
                      }
                      
                      return undefined;
                    }}
                    style={{
                      backgroundColor: '#333',
                      border: '1px solid #737373',
                      borderRadius: '4px',
                      color: 'white',
                      padding: '16px',
                      fontSize: '16px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ minHeight: '90px', marginBottom: '24px' }}>
                  <InputFormControl<FormData>
                    name="confirmPassword"
                    id="confirm_password" // DO NOT CHANGE: Used by E2E tests - https://github.netflix.net/corp/iac-login-flow-test/blob/main/src/main/kotlin/com/netflix/infosec/meechum/page/passwordreset/PasswordResetPage.kt
                    label={t('auth.resetPassword.confirmPasswordLabel')}
                    type="password"
                    validate={({ value, data }) => {
                      if (!value) return { message: t('auth.validation.confirmPasswordRequired'), type: 'error' };
                      if (value !== data.password) return { message: t('auth.validation.passwordsDoNotMatch'), type: 'error' };
                      return undefined;
                    }}
                    style={{
                      backgroundColor: '#333',
                      border: '1px solid #737373',
                      borderRadius: '4px',
                      color: 'white',
                      padding: '16px',
                      fontSize: '16px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {submitError && (
                  <ErrorDisplay error={submitError} />
                )}

                <Button
                  type="submit"
                  disabled={submitting || !formState.isValid || passwordValue !== confirmPasswordValue}
                  style={{
                    width: '100%',
                    backgroundColor: (submitting || !formState.isValid || passwordValue !== confirmPasswordValue) ? '#666' : '#e50914',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '700',
                    padding: '12px 16px',
                    cursor: (submitting || !formState.isValid || passwordValue !== confirmPasswordValue) ? 'not-allowed' : 'pointer',
                    height: '48px',
                    minHeight: '48px',
                    boxSizing: 'border-box'
                  }}
                >
                  {submitting ? t('auth.resetPassword.submittingButton') : t('auth.resetPassword.submitButton')}
                </Button>
              </form>
            </FormProvider>
          </Card>
        </div>
      </div>
    </NetflixGradientBackground>
  );
}

export default ResetPasswordForm;